import { DateTime } from 'luxon';

// Shared gateway-health domain logic used across the fleet-health and per-gateway
// history views, so bucket rules, colours and formatting live in one place.

// Severity colours, shared by every bucket of that severity. Buckets are graded by what an
// operator has to do about them, not by how many distinct states exist: the two "degraded"
// buckets read as one amber, and a Modbus link down is the same "this site has a fault"
// signal as a hardware alarm, so both are red. The label and tooltip carry the distinction.
const SEVERITY = {
    ok: '#448361',
    degraded: '#d9a441',
    fault: '#d44c47',
    offline: '#6b7178',
};

// Health bucket for a single hourly report, in display order. Each entry is the single
// source of truth for that bucket's wording and colour, so the fleet cards, the history
// chart, its legend, its tooltips and the detail pill cannot drift apart — and a new
// bucket cannot be half-added. `overviewKey` is the matching counter field in
// /gateway_health_reports/summary; `info` explains how a gateway lands in the bucket.
export const HEALTH_BUCKETS = {
    ok: {
        label: 'Fully operational',
        color: SEVERITY.ok,
        overviewKey: 'fully_operational',
        info: 'Connected, data coverage of at least 80%, no hardware alarms, and following setpoints well (score ≥ 70%).',
    },
    // Sits directly after "Fully operational" rather than in backend precedence order,
    // because the question it answers is "where did the rest of my working fleet go".
    // These gateways are up, connected and dispatchable — the fault is in the shape of
    // the telemetry, not the site — so reading the two cards side by side gives the
    // honest split: cleanly reporting, versus reporting off-contract.
    //
    // Drawn amber rather than red even though the backend ranks it *above* low cadence.
    // The ranking is about which reading to trust (malformed telemetry makes coverage,
    // status and the setpoint score meaningless, so it is the root cause to fix first);
    // the colour is about what an operator does, and that is file a firmware/integration
    // bug with the brand, not dispatch someone to a site. Same pattern the two red
    // buckets already use: shared colour, distinct label carries the distinction.
    bad_sample_format: {
        label: 'Operational, bad format',
        color: SEVERITY.degraded,
        overviewKey: 'bad_sample_format',
        info:
            'Connected and operating normally, but more than 5% of samples did not match the gateway ' +
            'sample contract — a missing or wrongly typed sensor, an SoC outside 0–1, or an inverted ' +
            'power_solar sign. The hardware is fine; the telemetry is not. Every other metric for ' +
            'this gateway reads as if the sensor were simply absent, so treat coverage, status and ' +
            'the setpoint score as untrustworthy until the integration is fixed.',
    },
    sampling_low: {
        label: 'Low sampling cadence',
        color: SEVERITY.degraded,
        overviewKey: 'sampling_cadence_low',
        info: 'Connected, but data coverage is below 80% — the gateway is reporting samples too infrequently.',
    },
    setpoint: {
        label: 'Setpoint deviation',
        color: SEVERITY.degraded,
        overviewKey: 'setpoint_deviation',
        info: 'Connected with good data, but the setpoint-following score is below 70% — not tracking commands closely.',
    },
    hardware_alarm: {
        label: 'Hardware alarm',
        color: SEVERITY.fault,
        overviewKey: 'hardware_alarm',
        info: 'Connected with good data, but the inverter or battery reported a hardware alarm, or the gateway status was error/warning.',
    },
    modbus_link_down: {
        label: 'Modbus link down',
        color: SEVERITY.fault,
        overviewKey: 'modbus_link_down',
        info: 'The Modbus link to the inverter was down for the period (more than 5% of samples reported the link as not working).',
    },
    offline: {
        label: 'Offline',
        color: SEVERITY.offline,
        overviewKey: 'offline',
        info: 'No samples received in the window, or the gateway was predominantly disconnected.',
    },
};

// The buckets as an ordered array for anything that iterates them (fleet cards, chart
// legend). Object key order above is the display order.
export const HEALTH_BUCKET_LIST = Object.entries(HEALTH_BUCKETS).map(([key, bucket]) => ({ key, ...bucket }));

// Coarser grouping for daily rollups — one entry per severity, so a day's stacked bar uses
// exactly the same four colours the hourly bars are drawn in.
export const HEALTH_GROUPS = [
    { key: 'ok', label: 'Fully operational', color: SEVERITY.ok },
    { key: 'degraded', label: 'Degraded', color: SEVERITY.degraded },
    { key: 'alarm', label: 'Hardware / Modbus fault', color: SEVERITY.fault },
    { key: 'offline', label: 'Offline', color: SEVERITY.offline },
];

// Fraction-of-samples threshold above which the inverter Modbus link is treated as
// down for the report. Mirrors the backend MODBUS_DOWN_THRESHOLD.
const MODBUS_DOWN_THRESHOLD = 0.05;

// Fraction-of-samples threshold above which the report's telemetry is treated as not
// matching the sample contract. Mirrors the backend BAD_SAMPLE_FORMAT_THRESHOLD, so a
// stray malformed sample cannot re-bucket an otherwise clean gateway.
const BAD_SAMPLE_FORMAT_THRESHOLD = 0.05;

export const COVERAGE_COLOR = '#3b6fb0';

// Classify one gateway_health_report into a bucket key. Precedence mirrors the
// backend fleet_overview classification
// (offline → modbus link down → bad sample format → low coverage → hardware alarm → setpoint).
export const classifyHealthReport = report => {
    const alarms = cleanAlarms(report.inverter_alarms).length + cleanAlarms(report.battery_alarms).length;
    const status = (report.gateway_status_dominant || '').toLowerCase();
    if ((report.num_samples ?? 0) === 0 || status === 'disconnected') {
        return 'offline';
    }
    // Genuine Modbus-link signal: the link was down for most of the period.
    if ((report.modbus_down_fraction ?? 0) > MODBUS_DOWN_THRESHOLD) {
        return 'modbus_link_down';
    }
    // Ahead of coverage and everything below it: a sensor that fails the contract reads as
    // *absent* in the rest of the report, so those metrics are not measuring what they claim.
    if ((report.bad_sample_format_fraction ?? 0) > BAD_SAMPLE_FORMAT_THRESHOLD) {
        return 'bad_sample_format';
    }
    if ((report.data_coverage_percentage ?? 1) < 0.8) {
        return 'sampling_low';
    }
    // error/warning gateway status is treated as a hardware fault, alongside alarms.
    if (alarms > 0 || status === 'error' || status === 'warning') {
        return 'hardware_alarm';
    }
    if ((report.setpoint_following_score ?? 1) < 0.7) {
        return 'setpoint';
    }
    return 'ok';
};

// Bucket key → coarse group key.
export const healthGroupOf = report => {
    const bucket = classifyHealthReport(report);
    if (bucket === 'ok') {
        return 'ok';
    }
    if (bucket === 'hardware_alarm' || bucket === 'modbus_link_down') {
        return 'alarm';
    }
    if (bucket === 'offline') {
        return 'offline';
    }
    return 'degraded';
};

// A 0–1 fraction as a whole-percent string, or an em dash when absent.
export const formatPct = value => (value == null ? '—' : `${Math.round(value * 100)}%`);

// inverter_alarms / battery_alarms are text[] of brand-specific strings, unioned per
// hour. They can contain '' (empty string = "no alarm") — the backend filters those
// too — so strip blanks before counting or displaying.
export const cleanAlarms = list => (list || []).filter(code => code && code.trim());

// Selectable look-back windows shared by the health views.
export const HEALTH_PERIODS = [
    { key: '24h', label: '24 hours', hours: 24 },
    { key: '7d', label: '7 days', hours: 24 * 7 },
    { key: '30d', label: '30 days', hours: 24 * 30 },
];

// ISO [from, to] window for the last `hours` up to now.
export const windowForHours = hours => {
    const now = DateTime.utc();
    return { from: now.minus({ hours }).toISO(), to: now.toISO() };
};

// Minutes after the top of the hour during which the just-closed hour's reports are
// still being written. The stats job starts at :02 and takes a couple of minutes to
// cover the fleet, and the summary endpoint only counts reports lying fully inside the
// window — so asking for that hour too early would show a partly-written fleet, or none.
export const REPORT_GRACE_MINUTES = 10;

// ISO [from, to] window covering the most recent complete clock hour — the "last hour"
// as far as health reports go. Reports are hourly and aligned to the clock hour, and
// /gateway_health_reports/summary only counts reports lying fully inside the window, so
// an unaligned [now - 1h, now] would match nothing except at the exact top of the hour.
// For the first REPORT_GRACE_MINUTES of an hour the hour before is used instead, so the
// section never comes up empty just because the page was opened as the hour turned.
export const lastCompleteHourWindow = (now = DateTime.utc()) => {
    const top = now.startOf('hour');
    const to = now.minute < REPORT_GRACE_MINUTES ? top.minus({ hours: 1 }) : top;
    return { from: to.minus({ hours: 1 }).toISO(), to: to.toISO() };
};
